
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { articleService } from '@/services/article';
import { useNavigate } from 'react-router-dom';

interface UseArticleOperationsProps {
  articleId: string | undefined;
  isNew: boolean;
  user: any;
}

export const useArticleOperations = ({ 
  articleId,
  isNew,
  user
}: UseArticleOperationsProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const saveArticle = async (articleData: any) => {
    console.log("useArticleOperations.saveArticle called", { articleId, isNew });
    
    if (isSaving) {
      console.log("Already saving, skipping");
      return false;
    }
    
    setIsSaving(true);
    try {
      // Check if user is authenticated
      if (!user?.id) {
        console.error("Authentication required");
        toast({
          title: "Authentication Required",
          description: "You must be logged in to save articles",
          variant: "destructive"
        });
        return false;
      }

      let result;
      
      // Create or update
      if (isNew) {
        console.log("Creating new article");
        result = await createArticle(articleData);
        
        if (result) {
          console.log("Article created with ID:", result);
          return result; // Return the article ID for new articles
        }
      } else {
        if (!articleId) {
          console.error("No article ID for update");
          return false;
        }
        console.log("Updating article:", articleId);
        result = await updateArticle(articleId, articleData);
      }
      
      if (result) {
        setLastSaved(new Date());
        return true;
      }
      return false;
    } catch (err: any) {
      console.error("Save article error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to save article",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const createArticle = async (articleData: any) => {
    try {
      console.log("Initializing storage buckets");
      // Initialize storage buckets first
      await articleService.initializeStorageBuckets();

      console.log("Creating article with data:", articleData);
      const { data, error } = await articleService.createArticle(articleData);

      if (error) {
        console.error("Article creation error:", error);
        toast({
          title: "Error",
          description: "Failed to create article: " + (error.message || "Unknown error"),
          variant: "destructive"
        });
        return false;
      }

      if (data?.id) {
        toast({
          title: "Success",
          description: "Article created successfully",
        });
        return data.id;
      } else {
        toast({
          title: "Error",
          description: "Failed to create article",
          variant: "destructive"
        });
        return false;
      }
    } catch (err: any) {
      console.error("Article creation error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to create article",
        variant: "destructive"
      });
      return false;
    }
  };

  const updateArticle = async (articleId: string, articleData: any) => {
    try {
      console.log("Updating article:", articleId, "with data:", articleData);
      const { data, error } = await articleService.updateArticle(articleId, articleData);

      if (error) {
        console.error("Article update error:", error);
        toast({
          title: "Error",
          description: "Failed to update article: " + (error.message || "Unknown error"),
          variant: "destructive"
        });
        return false;
      }

      console.log("Article updated successfully:", data);
      toast({
        title: "Success",
        description: "Article updated successfully",
      });
      return true;
    } catch (err: any) {
      console.error("Article update error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update article",
        variant: "destructive"
      });
      return false;
    }
  };

  const publishArticle = async (currentArticleId: string | undefined) => {
    if (!currentArticleId || isNew) {
      toast({
        title: "Save First",
        description: "Please save your article before publishing",
        variant: "default"
      });
      return false;
    }
    
    setIsPublishing(true);
    try {
      console.log("Publishing article:", currentArticleId);
      const { data, error } = await articleService.publishArticle(currentArticleId);

      if (error) {
        console.error("Article publish error:", error);
        toast({
          title: "Error",
          description: "Failed to publish article: " + (error.message || "Unknown error"),
          variant: "destructive"
        });
        return false;
      }
      
      console.log("Article published successfully:", data);
      toast({
        title: "Success",
        description: "Your article has been published",
        variant: "default"
      });
      
      return true;
    } catch (err: any) {
      console.error("Article publish error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to publish article",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    isSaving,
    isPublishing,
    lastSaved,
    saveArticle,
    publishArticle
  };
};
